import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { subscriptionsApi, businessPublicApi } from '../services/api';
import { showToast, askConfirm } from 'shared-ui';

interface Subscription {
  id: string;
  businessId?: string;
  businessName?: string;
  planId?: string;
  planName?: string;
  status?: string;
  nextBillingDate?: string;
  startDate?: string;
  cancelledAt?: string;
  pausedAt?: string;
  pauseResumeAt?: string;
  cancelAtEndOfPeriod?: boolean;
  pendingCancellationAt?: string;
  paymentStatus?: string;
  cancelAfterMonths?: number;
  scheduledCancelDate?: string;
  amount?: number;
  currency?: string;
  billingCycle?: string;
}

interface Summary { totalDueNextPeriod?: number; nextBillingDate?: string | null; unpaidCount?: number; activeCount?: number; }

const Subscriptions: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [summary, setSummary] = useState<Summary>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'current' | 'past'>('current');
  const [actioning, setActioning] = useState<string | null>(null);
  const [pauseModal, setPauseModal] = useState<{ sub: Subscription } | null>(null);
  const [cancelModal, setCancelModal] = useState<{ sub: Subscription } | null>(null);
  const [cancelChoice, setCancelChoice] = useState<'immediate' | 'endOfPeriod'>('endOfPeriod');
  const [pauseType, setPauseType] = useState<'indefinite' | 'until'>('indefinite');
  const [pauseUntil, setPauseUntil] = useState('');
  const [subscribeModal, setSubscribeModal] = useState(false);
  const [subscribeBusinessId, setSubscribeBusinessId] = useState('');
  const [subscribePlanName, setSubscribePlanName] = useState('');
  const [subscribeCancelAfter, setSubscribeCancelAfter] = useState<number | ''>('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [businessSearch, setBusinessSearch] = useState('');
  const [selectedBusinessName, setSelectedBusinessName] = useState('');
  const [businessResults, setBusinessResults] = useState<{ id: string; name: string; mangoId?: string }[]>([]);
  const [businessSearching, setBusinessSearching] = useState(false);
  const [search, setSearch] = useState('');
  const mountedRef = useRef(true);
  const businessIdFilter = searchParams.get('businessId') || '';

  const loadSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await subscriptionsApi.getMySubscriptions();
      if (!mountedRef.current) return;
      if (res?.success && Array.isArray(res?.data)) {
        setSubscriptions(res.data);
        setSummary(res.summary || {});
      } else if (res?.subscriptions) {
        setSubscriptions(Array.isArray(res.subscriptions) ? res.subscriptions : []);
      } else {
        setSubscriptions([]);
        setSummary({});
        if (res?.error && res.error !== 'Not found') setError(res.error);
      }
    } catch (err) {
      console.error('Load subscriptions failed:', err);
      if (mountedRef.current) setSubscriptions([]);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    loadSubscriptions();
    return () => { mountedRef.current = false; };
  }, [loadSubscriptions]);

  const matchesSearchAndBusiness = useCallback((sub: Subscription) => {
    const businessName = (sub.businessName || sub.planName || '').toLowerCase();
    const query = search.trim().toLowerCase();
    const matchesQuery = !query || businessName.includes(query);
    const matchesBusiness = !businessIdFilter || sub.businessId === businessIdFilter;
    return matchesQuery && matchesBusiness;
  }, [search, businessIdFilter]);

  const current = subscriptions.filter((s) => (s.status === 'active' || s.status === 'paused' || s.cancelAtEndOfPeriod) && matchesSearchAndBusiness(s));
  const past = subscriptions.filter((s) => s.status === 'cancelled' && matchesSearchAndBusiness(s));

  const handleCancelClick = (sub: Subscription) => {
    setCancelModal({ sub });
    setCancelChoice('endOfPeriod');
  };

  const handleCancelConfirm = async () => {
    if (!cancelModal) return;
    if (cancelChoice === 'endOfPeriod' && cancelModal.sub.nextBillingDate) {
      const dateStr = new Date(cancelModal.sub.nextBillingDate).toLocaleDateString();
      if (!(await askConfirm({ title: 'Cancel this subscription?', message: `You'll keep access until ${dateStr}.`, confirmLabel: 'Cancel subscription', cancelLabel: 'Keep it', danger: true }))) return;
    }
    setActioning(cancelModal.sub.id);
    try {
      const res = await subscriptionsApi.cancelSubscription(cancelModal.sub.id, cancelChoice === 'endOfPeriod');
      if (res?.success) {
        setCancelModal(null);
        await loadSubscriptions();
      } else showToast(res?.error || 'Failed to cancel', 'error');
    } catch { showToast('Failed to cancel', 'error'); }
    finally { setActioning(null); }
  };

  const formatPeriod = (cycle?: string) => {
    if (!cycle) return '';
    return cycle === 'monthly' ? '/month' : cycle === 'yearly' ? '/year' : cycle === 'weekly' ? '/week' : `/${cycle}`;
  };

  const handleSetCancelAfter = async (sub: Subscription, months: number | null) => {
    setActioning(sub.id);
    try {
      const res = await subscriptionsApi.setCancelAfter(sub.id, months);
      if (res?.success) await loadSubscriptions();
      else showToast(res?.error || 'Failed to update', 'error');
    } catch { showToast('Failed to update', 'error'); }
    finally { setActioning(null); }
  };

  const handlePauseClick = (sub: Subscription) => {
    setCancelModal(null);
    setPauseModal({ sub });
    setPauseType('indefinite');
    setPauseUntil('');
  };

  const handlePauseConfirm = async () => {
    if (!pauseModal) return;
    const { sub } = pauseModal;
    setActioning(sub.id);
    try {
      const options = pauseType === 'until' && pauseUntil ? { resumeAt: pauseUntil } : undefined;
      const res = await subscriptionsApi.pauseSubscription(sub.id, options);
      if (res?.success) {
        setPauseModal(null);
        await loadSubscriptions();
      } else {
        showToast(res?.error || 'Failed to pause', 'error');
      }
    } catch {
      showToast('Failed to pause subscription', 'error');
    } finally {
      setActioning(null);
    }
  };

  useEffect(() => {
    if (!subscribeModal || !businessSearch.trim()) {
      setBusinessResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setBusinessSearching(true);
      try {
        const res = await businessPublicApi.list({ search: businessSearch.trim(), limit: 10 });
        if (res?.success && Array.isArray(res?.data)) {
          setBusinessResults(res.data.map((b: { id: string; name: string; mangoId?: string }) => ({ id: b.id, name: b.name, mangoId: b.mangoId })));
        } else setBusinessResults([]);
      } catch { setBusinessResults([]); }
      finally { setBusinessSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [subscribeModal, businessSearch]);

  const closeSubscribeModal = () => {
    if (!actioning) {
      setSubscribeModal(false);
      setSubscribeBusinessId('');
      setSubscribePlanName('');
      setSubscribeCancelAfter('');
      setBusinessSearch('');
      setSelectedBusinessName('');
      setBusinessResults([]);
    }
  };

  const getCancelAfterEndDate = (months: number) => {
    const d = new Date();
    d.setMonth(d.getMonth() + months);
    return d.toLocaleDateString();
  };

  const formatMoney = (value: unknown) => {
    const numeric = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numeric) ? numeric.toFixed(2) : '0.00';
  };

  const handleSubscribe = async () => {
    if (!subscribeBusinessId.trim() || !subscribePlanName.trim()) {
      showToast('Please enter business ID and plan name', 'error');
      return;
    }
    setActioning('subscribe');
    try {
      const res = await subscriptionsApi.subscribeToBusiness(subscribeBusinessId.trim(), subscribePlanName.trim(), {
        cancelAfterMonths: subscribeCancelAfter === '' ? undefined : (subscribeCancelAfter as number),
      });
      if (res?.success) {
        closeSubscribeModal();
        setSuccessMessage('Subscribed successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
        await loadSubscriptions();
      } else {
        showToast(res?.error || 'Failed to subscribe', 'error');
      }
    } catch {
      showToast('Failed to subscribe', 'error');
    } finally {
      setActioning(null);
    }
  };

  const handleReactivate = async (id: string) => {
    setActioning(id);
    try {
      const res = await subscriptionsApi.reactivateSubscription(id);
      if (res?.success) await loadSubscriptions();
      else showToast(res?.error || 'Failed to reactivate', 'error');
    } catch {
      showToast('Failed to reactivate subscription', 'error');
    } finally {
      setActioning(null);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 32 }}>🔄</div>
        <p style={{ color: '#666', marginTop: 12 }}>Loading subscriptions...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>🔄 My Subscriptions</h1>
      <p style={{ margin: '10px 0 16px 0', color: '#666' }}>
        View who you subscribe to, period, amount. Deactivate or reactivate anytime.
      </p>
      {successMessage && (
        <div style={{ padding: 12, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, marginBottom: 16, color: '#166534' }}>
          {successMessage}
        </div>
      )}
      {(summary.activeCount ?? 0) > 0 && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ padding: '12px 20px', background: '#fff7ed', borderRadius: 10, border: '1px solid #fed7aa' }}>
            <span style={{ fontSize: 12, color: '#9a3412' }}>Next bill</span>
            <div style={{ fontWeight: 700, fontSize: 18, color: '#9a3412' }}>${formatMoney(summary.totalDueNextPeriod)}</div>
            {summary.nextBillingDate && <span style={{ fontSize: 12, color: '#c2410c' }}>{new Date(summary.nextBillingDate).toLocaleDateString()}</span>}
          </div>
          {(summary.unpaidCount ?? 0) > 0 && (
            <div style={{ padding: '12px 20px', background: '#fef2f2', borderRadius: 10, border: '1px solid #fecaca' }}>
              <span style={{ fontSize: 12, color: '#b91c1c' }}>Unpaid</span>
              <div style={{ fontWeight: 700, fontSize: 18, color: '#b91c1c' }}>{summary.unpaidCount}</div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ padding: 16, background: '#fef2f2', color: '#b91c1c', borderRadius: 10, marginBottom: 20 }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search subscriptions by business..."
          style={{ width: '100%', maxWidth: 340, padding: '10px 12px', border: '1px solid #ddd', borderRadius: 10 }}
        />
        {businessIdFilter && (
          <button
            onClick={() => navigate('/subscriptions')}
            style={{ padding: '10px 14px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, color: '#9a3412', cursor: 'pointer', fontWeight: 600 }}
          >
            Clear Business Filter
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          onClick={() => setActiveTab('current')}
          style={{
            padding: '10px 20px',
            background: activeTab === 'current' ? 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)' : '#fff',
            color: activeTab === 'current' ? '#fff' : '#333',
            border: '1px solid #ddd',
            borderRadius: 8,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Current ({current.length})
        </button>
        <button
          onClick={() => setActiveTab('past')}
          style={{
            padding: '10px 20px',
            background: activeTab === 'past' ? 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)' : '#fff',
            color: activeTab === 'past' ? '#fff' : '#333',
            border: '1px solid #ddd',
            borderRadius: 8,
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Past ({past.length})
        </button>
      </div>

      {activeTab === 'current' && (
        <>
          {current.length === 0 ? (
            <div style={{ background: 'white', padding: 50, borderRadius: 12, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <span style={{ fontSize: 48 }}>🔄</span>
              <p style={{ color: '#666', marginTop: 15 }}>No active subscriptions</p>
              <p style={{ color: '#999', fontSize: 14, marginBottom: 20 }}>When you subscribe to a business or plan, it will appear here.</p>
              <button
                onClick={() => setSubscribeModal(true)}
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #ff9a56 0%, #ff6b35 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                + Subscribe to a business
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <button
                onClick={() => setSubscribeModal(true)}
                style={{
                  alignSelf: 'flex-start',
                  padding: '8px 16px',
                  background: '#f0fdf4',
                  color: '#166534',
                  border: '1px solid #86efac',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                + Add subscription
              </button>
              {current.map((sub) => (
                <div
                  key={sub.id}
                  style={{
                    background: 'white',
                    padding: 20,
                    borderRadius: 12,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <h3 style={{ margin: 0, fontSize: 18 }}>{sub.businessName || sub.planName || 'Subscription'}</h3>
                        {['overdue','failed','past_due'].includes(sub.paymentStatus ?? '') && (
                          <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: '#fef2f2', color: '#b91c1c' }}>Unpaid</span>
                        )}
                        {sub.cancelAtEndOfPeriod && (
                          <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 12, background: '#fef3c7', color: '#b45309' }}>Cancelling {sub.nextBillingDate ? new Date(sub.nextBillingDate).toLocaleDateString() : ''}</span>
                        )}
                        {sub.scheduledCancelDate && !sub.cancelAtEndOfPeriod && (
                          <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 12, background: '#e0e7ff', color: '#3730a3' }}>Auto-cancels {new Date(sub.scheduledCancelDate).toLocaleDateString()}</span>
                        )}
                      </div>
                      <p style={{ margin: '6px 0 0 0', color: '#666', fontSize: 14 }}>
                        {sub.billingCycle ? `${sub.billingCycle.charAt(0).toUpperCase() + sub.billingCycle.slice(1)}` : '—'} •{' '}
                        {sub.nextBillingDate ? `Next: ${new Date(sub.nextBillingDate).toLocaleDateString()}` : '—'}
                        {sub.status === 'paused' && sub.pauseResumeAt && (
                          <span style={{ marginLeft: 8, color: '#f59e0b' }}>
                            • Resumes {new Date(sub.pauseResumeAt).toLocaleDateString()}
                          </span>
                        )}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontWeight: 700, fontSize: 20, color: '#333' }}>
                        ${formatMoney(sub.amount)}
                        {formatPeriod(sub.billingCycle)}
                      </span>
                      {sub.currency && sub.currency !== 'AUD' && (
                        <span style={{ fontSize: 12, color: '#888', marginLeft: 4 }}>{sub.currency}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {sub.status === 'paused' ? (
                      <button
                        onClick={() => handleReactivate(sub.id)}
                        disabled={!!actioning}
                        style={{
                          padding: '8px 16px',
                          background: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: 8,
                          cursor: actioning ? 'not-allowed' : 'pointer',
                          fontWeight: 500,
                        }}
                      >
                        {actioning === sub.id ? '…' : 'Reactivate'}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handlePauseClick(sub)}
                          disabled={!!actioning}
                          style={{
                            padding: '8px 16px',
                            background: '#f0ad4e',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            cursor: actioning ? 'not-allowed' : 'pointer',
                            fontWeight: 500,
                          }}
                        >
                          {actioning === sub.id ? '…' : 'Pause'}
                        </button>
                        <select
                          value={sub.cancelAfterMonths ?? ''}
                          onChange={(e) => handleSetCancelAfter(sub, e.target.value === '' ? null : parseInt(e.target.value, 10))}
                          disabled={!!actioning}
                          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 }}
                        >
                          <option value="">No auto-cancel</option>
                          {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                            <option key={m} value={m}>Cancel after {m} mo</option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleCancelClick(sub)}
                          disabled={!!actioning}
                          style={{
                            padding: '8px 16px',
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            cursor: actioning ? 'not-allowed' : 'pointer',
                            fontWeight: 500,
                          }}
                        >
                          {actioning === sub.id ? '…' : 'Deactivate'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'past' && (
        <>
          {past.length === 0 ? (
            <div style={{ background: 'white', padding: 50, borderRadius: 12, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <span style={{ fontSize: 48 }}>📜</span>
              <p style={{ color: '#666', marginTop: 15 }}>No past subscriptions</p>
              <p style={{ color: '#999', fontSize: 14 }}>Cancelled subscriptions will appear here.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {past.map((sub) => (
                <div
                  key={sub.id}
                  style={{
                    background: 'white',
                    padding: 20,
                    borderRadius: 12,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    opacity: 0.9,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
                    <div>
                      <h3 style={{ margin: '0 0 4px 0', fontSize: 18 }}>{sub.businessName || sub.planName || 'Subscription'}</h3>
                      <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
                        {sub.billingCycle ? `${sub.billingCycle.charAt(0).toUpperCase() + sub.billingCycle.slice(1)}` : '—'} •{' '}
                        Cancelled {sub.cancelledAt ? new Date(sub.cancelledAt).toLocaleDateString() : '—'}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span style={{ fontWeight: 700, fontSize: 20, color: '#666' }}>
                        ${formatMoney(sub.amount)}
                        {formatPeriod(sub.billingCycle)}
                      </span>
                    </div>
                  </div>
                  <div style={{ marginTop: 16 }}>
                    <button
                      onClick={() => handleReactivate(sub.id)}
                      disabled={!!actioning}
                      style={{
                        padding: '8px 16px',
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        cursor: actioning ? 'not-allowed' : 'pointer',
                        fontWeight: 500,
                      }}
                    >
                      {actioning === sub.id ? '…' : 'Reactivate'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {cancelModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001 }} onClick={() => !actioning && setCancelModal(null)}>
          <div style={{ background: 'white', padding: 24, borderRadius: 12, maxWidth: 420, width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0' }}>Cancel subscription</h3>
            <p style={{ margin: '0 0 16px 0', color: '#666' }}>&quot;{cancelModal.sub.businessName || cancelModal.sub.planName}&quot;</p>
            <p style={{ margin: '0 0 12px 0', fontSize: 14, color: '#059669' }}>Consider pausing instead — you keep access and can reactivate anytime.</p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8 }}>
                <input type="radio" checked={cancelChoice === 'endOfPeriod'} onChange={() => setCancelChoice('endOfPeriod')} />
                <span style={{ marginLeft: 8 }}>Cancel at end of billing period</span>
                <span style={{ marginLeft: 8, fontSize: 12, color: '#6b7280' }}>(keep access until {cancelModal.sub.nextBillingDate ? new Date(cancelModal.sub.nextBillingDate).toLocaleDateString() : 'next bill'})</span>
              </label>
              <label style={{ display: 'block' }}>
                <input type="radio" checked={cancelChoice === 'immediate'} onChange={() => setCancelChoice('immediate')} />
                <span style={{ marginLeft: 8 }}>Cancel immediately</span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button onClick={() => { setCancelModal(null); handlePauseClick(cancelModal.sub); }} style={{ padding: '8px 16px', background: '#f0ad4e', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Pause instead</button>
              <button onClick={() => setCancelModal(null)} style={{ padding: '8px 16px', background: '#e5e7eb', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Keep</button>
              <button onClick={handleCancelConfirm} disabled={!!actioning} style={{ padding: '8px 16px', background: '#dc3545', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}>{actioning ? '…' : 'Cancel'}</button>
            </div>
          </div>
        </div>
      )}

      {pauseModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => !actioning && setPauseModal(null)}
        >
          <div
            style={{
              background: 'white',
              padding: 24,
              borderRadius: 12,
              maxWidth: 400,
              width: '90%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0' }}>Pause subscription</h3>
            <p style={{ margin: '0 0 16px 0', color: '#666' }}>Pause &quot;{pauseModal.sub.businessName || pauseModal.sub.planName}&quot;</p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8 }}>
                <input
                  type="radio"
                  checked={pauseType === 'indefinite'}
                  onChange={() => setPauseType('indefinite')}
                />
                <span style={{ marginLeft: 8 }}>Indefinite</span>
              </label>
              <label style={{ display: 'block' }}>
                <input
                  type="radio"
                  checked={pauseType === 'until'}
                  onChange={() => setPauseType('until')}
                />
                <span style={{ marginLeft: 8 }}>Until date</span>
              </label>
            </div>
            {pauseType === 'until' && (
              <div style={{ marginBottom: 16 }}>
                <input
                  type="date"
                  value={pauseUntil}
                  onChange={(e) => setPauseUntil(e.target.value)}
                  style={{ padding: 8, borderRadius: 8, border: '1px solid #ddd', width: '100%' }}
                />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setPauseModal(null)}
                style={{ padding: '8px 16px', background: '#e5e7eb', border: 'none', borderRadius: 8, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handlePauseConfirm}
                disabled={!!actioning || (pauseType === 'until' && !pauseUntil)}
                style={{
                  padding: '8px 16px',
                  background: '#f0ad4e',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: actioning || (pauseType === 'until' && !pauseUntil) ? 'not-allowed' : 'pointer',
                }}
              >
                {actioning ? '…' : 'Pause'}
              </button>
            </div>
          </div>
        </div>
      )}

      {subscribeModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeSubscribeModal}
        >
          <div
            style={{ background: 'white', width: '90%', maxWidth: 420, borderRadius: 12, padding: 20, boxShadow: '0 12px 35px rgba(0,0,0,0.2)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 12px 0', fontSize: 18, color: '#333' }}>Subscribe to a business</h3>
            <p style={{ margin: '0 0 16px 0', fontSize: 14, color: '#666' }}>
              Search for a business or enter the ID from their profile. Then choose a plan and optional auto-cancel.
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Search or Business ID</label>
              <input
                type="text"
                value={selectedBusinessName || businessSearch || subscribeBusinessId}
                onChange={(e) => {
                  setSelectedBusinessName('');
                  setBusinessSearch(e.target.value);
                  setSubscribeBusinessId(e.target.value);
                }}
                placeholder="Search by name or enter ID (e.g. biz_abc123)"
                style={{ padding: 10, borderRadius: 8, border: '1px solid #ddd', width: '100%' }}
              />
              <p style={{ margin: '4px 0 0 0', fontSize: 11, color: '#9ca3af' }}>Find the ID on the business profile or in their subscribe link.</p>
              {businessResults.length > 0 && (
                <ul style={{ margin: '8px 0 0 0', padding: 0, listStyle: 'none', maxHeight: 120, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                  {businessResults.map((b) => (
                    <li
                      key={b.id}
                      onClick={() => { setSubscribeBusinessId(b.id); setSelectedBusinessName(b.name); setBusinessSearch(''); setBusinessResults([]); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                    >
                      {b.name} <span style={{ fontSize: 11, color: '#9ca3af' }}>({b.id})</span>
                    </li>
                  ))}
                </ul>
              )}
              {businessSearching && <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6b7280' }}>Searching…</p>}
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Plan name</label>
              <input
                type="text"
                value={subscribePlanName}
                onChange={(e) => setSubscribePlanName(e.target.value)}
                placeholder="e.g. basic, premium"
                style={{ padding: 10, borderRadius: 8, border: '1px solid #ddd', width: '100%' }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>Auto-cancel after (optional)</label>
              <select
                value={subscribeCancelAfter}
                onChange={(e) => setSubscribeCancelAfter(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                style={{ padding: 10, borderRadius: 8, border: '1px solid #ddd', width: '100%' }}
              >
                <option value="">No auto-cancel</option>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                  <option key={m} value={m}>Cancel after {m} month{m > 1 ? 's' : ''}</option>
                ))}
              </select>
              {subscribeCancelAfter !== '' && (
                <p style={{ margin: '6px 0 0 0', fontSize: 13, color: '#059669', fontWeight: 500 }}>
                  Subscription will end around {getCancelAfterEndDate(subscribeCancelAfter as number)}.
                </p>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={closeSubscribeModal} style={{ padding: '8px 16px', background: '#e5e7eb', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
              <button
                onClick={handleSubscribe}
                disabled={!!actioning}
                style={{ padding: '8px 16px', background: '#ff6b35', color: 'white', border: 'none', borderRadius: 8, cursor: actioning ? 'not-allowed' : 'pointer' }}
              >
                {actioning === 'subscribe' ? '…' : 'Subscribe'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscriptions;
